import React, { createContext, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { useDashboardParam } from 'utils/hooks/useDashboardParam'

/**
 * AppLayout - CSS Grid-based layout for the application
 *
 * This layout eliminates the need for hardcoded heights by using CSS Grid
 * where the header (banner + navigation) auto-sizes based on content,
 * and the content area fills the remaining space.
 *
 * Structure:
 * ┌─────────────────────────────────────┐
 * │ [Banner - auto height]              │  ← Only renders if provided
 * ├─────────────────────────────────────┤
 * │ [Navigation - 70px via CSS var]     │  ← Sticky header
 * ├─────────────────────────────────────┤
 * │ [Content - fills remaining space]   │  ← Scrollable
 * └─────────────────────────────────────┘
 */

export const ScrollContext = createContext(null)

const AppLayoutWrapper = styled.div`
  display: grid;
  /* IC-51160: auto column min-size is the nav's min-content, so a packed old
     top bar blew the whole app wider than the window and overflow:hidden
     clipped Reports + the email. */
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  height: 100vh;
  overflow: hidden;
`

const HeaderArea = styled.header`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  z-index: var(--z-index-navigation);
  min-width: 0;
`

const ContentArea = styled.main.attrs({ className: 'app-content' })`
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
  min-width: 0;
  background: var(--color-background);
`

const AppLayout = ({ banner, navigation, children }) => {
  const scrollRef = useRef(null)
  const location = useLocation()

  useDashboardParam()

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo(0, 0)
    }
  }, [location.pathname])

  return (
    <ScrollContext.Provider value={scrollRef}>
      <AppLayoutWrapper>
        <HeaderArea>
          {banner}
          {navigation}
        </HeaderArea>
        <ContentArea ref={scrollRef}>{children}</ContentArea>
      </AppLayoutWrapper>
    </ScrollContext.Provider>
  )
}

export default AppLayout
